import { DtoExtension, DtoFileExtension } from '../constants/dto';

interface ResolveBackendFileExtensionInput {
  fileName?: string;
  detectedExtension?: string | null;
  fallbackExtension?: string | null;
  allowedExtensions?: readonly string[];
}

const MP4_COMPATIBLE_BRAND_EXTENSIONS = new Set(['m4v']);
const MP4_COMPATIBLE_FILE_EXTENSIONS = new Set(['mp4', 'mov']);

const getExtensionFromFileName = (fileName?: string): string => {
  if (!fileName) return '';
  const lastDotIndex = fileName.lastIndexOf('.');
  if (lastDotIndex <= 0 || lastDotIndex === fileName.length - 1) return '';
  return fileName.slice(lastDotIndex + 1).toLowerCase();
};

const normalizeExtension = (extension?: string | null): string =>
  extension?.trim().replace(/^\./, '').toLowerCase() ?? '';

const resolveExtensionName = ({
  fileName,
  detectedExtension,
  fallbackExtension,
}: ResolveBackendFileExtensionInput): string => {
  const originalExtension = getExtensionFromFileName(fileName);
  const detected = normalizeExtension(detectedExtension);
  const fallback = normalizeExtension(fallbackExtension);

  if (
    MP4_COMPATIBLE_BRAND_EXTENSIONS.has(detected) &&
    MP4_COMPATIBLE_FILE_EXTENSIONS.has(originalExtension)
  ) {
    return originalExtension;
  }

  return detected || fallback || originalExtension;
};

const assertAllowedExtension = (
  extension: string,
  allowedExtensions?: readonly string[],
): void => {
  if (!allowedExtensions) return;
  const allowed = new Set(allowedExtensions.map(normalizeExtension));
  if (!allowed.has(extension)) {
    throw new Error(
      `Unsupported file extension for this project: ${extension}`,
    );
  }
};

const resolveCode = <T extends Record<string, number>>(
  extensionMap: T,
  extension: string,
): T[keyof T] => {
  const code = extensionMap[extension as keyof T];
  if (code === undefined) {
    throw new Error(`Unsupported file extension: ${extension}`);
  }
  return code;
};

export const resolveBackendFileExtensionName = (
  input: ResolveBackendFileExtensionInput,
): string => resolveExtensionName(input);

export const resolveBackendDtoExtension = (
  input: ResolveBackendFileExtensionInput,
) => {
  const extension = resolveExtensionName(input);
  return resolveCode(DtoExtension, extension);
};

export const resolveBackendProjectFileExtension = (
  input: ResolveBackendFileExtensionInput,
) => {
  const extension = resolveExtensionName(input);
  assertAllowedExtension(extension, input.allowedExtensions);
  return resolveCode(DtoFileExtension, extension);
};
